import { FC } from 'react';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { type SlideInfo } from 'streams/slides/slideOperations';
import type { MetaInfo } from 'streams/views/mainContent/types';
import { ModalSize, info } from '@ardoq/modal';
import { s8 } from '@ardoq/design-tokens';
import { DoqFace, DoqSize } from '@ardoq/doq';
import { ViewMigrationGuideLink } from 'admin/migrationChecklist/atoms';
import { IconName } from '@ardoq/icons';
import {
  APIPresentationAssetAttributes,
  APIPresentationAttributes,
  PresentationReadPermissions,
  ViewIds,
} from '@ardoq/api-types';
import { toPresentationAsset } from 'presentation/toPresentationAsset';
import { expressTimespan } from 'viewDeprecation/components/DeprecatedSlidesNotification';
import {
  AlertMessageContainer,
  BodyContainer,
  CallToActionMessageContainer,
  ContactYourAdminLink,
  DoqFaceContainer,
  LeftBoundFlexContainerNoTextWrap,
  SlideCreatedByName,
  StyledIconForDeprecationCountdown,
} from 'viewDeprecation/atoms';
import { getDoqTypeForView } from './util';
import { connect } from '@ardoq/rxbeach';
import { SlideCreatorDetails } from './components/DeprecatedSlidesNotificationParts';
import { currentDate } from '@ardoq/date-time';
import { PayloadLoadSlide } from '../presentation/viewPane/actions';
import {
  presentationPermissionOperations,
  type PermissionContext,
} from '@ardoq/access-control';
import { CanCreatorMigrateSlidesInfo } from '@ardoq/api';
import { deprecatedSlides$ } from './deprecatedSlides$';

type DisplayCountdownForDeprecatedSlidesArgs = {
  slide: PayloadLoadSlide;
  presentation:
    | APIPresentationAttributes
    | APIPresentationAssetAttributes
    | undefined;
  slideInfo: SlideInfo | null;
  permissionContext: PermissionContext;
  deprecatedViews: MetaInfo[];
};

export const displayCountdownForDeprecatedSlides = ({
  slide,
  presentation,
  slideInfo,
  permissionContext,
  deprecatedViews,
}: DisplayCountdownForDeprecatedSlidesArgs) => {
  if (slide.viewId) {
    const deprecatedView = deprecatedViews.find(
      ({ id }) => id === slide.viewId
    );

    const killDate = deprecatedView?.killDate;

    const now = currentDate();

    if (killDate && killDate > now && slideInfo) {
      const timeSpan = expressTimespan(now, killDate);
      // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders info() body once and does not re-render on locale change
      const i18nLocale = i18nLocale$.state;
      info({
        title: t(
          i18nLocale,
          'viewDeprecation:countdown.title',
          { timeSpan },
          'View will be discontinued in {{timeSpan}}'
        ),
        modalSize: ModalSize.S,
        primaryButtonText: t(
          i18nLocale,
          'viewDeprecation:countdown.okButton',
          'Ok'
        ),
        onPrimaryButtonClick: () => true,
        body: (
          <DeprecationCountdownDialogBody
            viewId={slide.viewId}
            timeSpan={timeSpan}
            createdByName={slideInfo.createdByName}
            createdByEmail={slideInfo.createdByEmail}
            viewName={deprecatedView?.name || slide.viewId}
            hasPermissions={Boolean(
              slide.hasFullAccess &&
              presentation &&
              presentationPermissionOperations.canEditPresentation(
                permissionContext,
                toPresentationAsset(presentation)
              )
            )}
            slideId={slide.id}
          />
        ),
      });
    }
  }
};

type DeprecationCountdownDialogBodyProps = {
  slidesInfo?: CanCreatorMigrateSlidesInfo;
} & {
  viewId: ViewIds;
  hasPermissions: boolean;
  timeSpan: string;
  createdByName: string;
  createdByEmail: string;
  viewName: string;
  slideId: string;
};

const DeprecationCountdownDialogBody = (
  props: DeprecationCountdownDialogBodyProps
) => (
  <BodyContainer>
    <DoqFaceContainer>
      <DoqFace
        doqType={getDoqTypeForView(props.viewId)}
        doqSize={DoqSize.SMALL}
      />
    </DoqFaceContainer>

    <div>
      <CallToActionMessageContainer>
        <ConnectedCallToActionMessageForDeprecatedSlide {...props} />
      </CallToActionMessageContainer>
      <ConnectedAlertMessage timeSpan={props.timeSpan} />
    </div>
  </BodyContainer>
);

type AlertMessageProps = {
  timeSpan: string;
  i18nLocale: I18nLocale;
};
const AlertMessage = ({ timeSpan, i18nLocale }: AlertMessageProps) => (
  <AlertMessageContainer>
    <StyledIconForDeprecationCountdown iconName={IconName.WARNING} />
    {t(
      i18nLocale,
      'viewDeprecation:countdown.alertMessage',
      { timeSpan },
      'View will be removed in {{timeSpan}}'
    )}
  </AlertMessageContainer>
);

const ConnectedAlertMessage = connect(
  AlertMessage,
  i18nLocale$.pipe(map(i18nLocale => ({ i18nLocale })))
);

type CallToActionExtras = {
  isPublic: boolean;
  i18nLocale: I18nLocale;
};

const CallToActionMessageForDeprecatedSlide: FC<
  DeprecationCountdownDialogBodyProps & CallToActionExtras
> = ({
  hasPermissions,
  createdByName,
  createdByEmail,
  viewName,
  slideId,
  slidesInfo,
  isPublic,
  i18nLocale,
}) => {
  const showCreatorDetails =
    !slidesInfo || !!slidesInfo?.[slideId]?.creatorCanMigrate;
  return hasPermissions ? (
    <>
      <div>
        {t(
          i18nLocale,
          'viewDeprecation:countdown.migrateSlide',
          { viewName },
          'Migrate the slide to the new {{viewName}} View.'
        )}
      </div>
      <ViewMigrationGuideLink typography="text2">
        {t(i18nLocale, 'viewDeprecation:countdown.learnHow', 'Learn how')}
      </ViewMigrationGuideLink>
    </>
  ) : showCreatorDetails && !isPublic ? (
    <>
      <div style={{ marginBottom: s8 }}>
        {t(
          i18nLocale,
          'viewDeprecation:countdown.contactCreator',
          'Contact the slide creator to migrate slide to the new view:'
        )}
      </div>
      <SlideCreatorDetails
        createdByName={createdByName}
        createdByEmail={createdByEmail}
        showCreatorDetails={showCreatorDetails}
        i18nLocale={i18nLocale}
      />
    </>
  ) : isPublic ? (
    <div>
      {showCreatorDetails
        ? t(
            i18nLocale,
            'viewDeprecation:countdown.contactByCreator',
            'The view used in this slide has been updated and needs to be migrated by the slide creator.'
          )
        : t(
            i18nLocale,
            'viewDeprecation:countdown.contactByAdmin',
            'The view used in this slide has been updated and needs to be migrated by your admin.'
          )}
    </div>
  ) : (
    <LeftBoundFlexContainerNoTextWrap>
      <SlideCreatedByName>
        <ContactYourAdminLink i18nLocale={i18nLocale} />
        <span>
          &nbsp;
          {t(
            i18nLocale,
            'viewDeprecation:countdown.contactAdminSuffix',
            'to migrate slides to the new view.'
          )}
        </span>
      </SlideCreatedByName>
    </LeftBoundFlexContainerNoTextWrap>
  );
};

const ConnectedCallToActionMessageForDeprecatedSlide = connect(
  CallToActionMessageForDeprecatedSlide,
  combineLatest([deprecatedSlides$, i18nLocale$]).pipe(
    map(([{ slidesInfo, activePresentation }, i18nLocale]) => ({
      slidesInfo,
      isPublic:
        activePresentation?.readAccess === PresentationReadPermissions.ALL,
      i18nLocale,
    }))
  )
);
